import { RuleContext } from '@alfresco/adf-extensions';
import { NodeEntry, UserRepresentation } from '@alfresco/js-api';
import { take } from 'rxjs/operators';
import find from 'lodash/find';

import {
  AcaRuleContext,
  canAddFavorite,
  canRemoveFavorite,
  canUploadVersion,
  isContentServiceEnabled,
  canOpenWithOffice,
} from '@alfresco/aca-shared/rules';
import * as navigation from '@alfresco/aca-shared/rules';
import { AllowableOperationsEnum } from '@alfresco/adf-content-services';

import { CommonConstants, LocalStorage } from '../../shared';
import {
  getApsUserProfile,
  isAdmin,
  getCurrentSiteUserRole,
  getFeatureFlags,
} from '../../store/selectors/app.custom-selectors';
import { getDocumentValidationStatus } from '../../modules/wk-document-decorator/state/selectors/get.documentvalidationstatus';
import { AppConstants } from '../../components/shared/constants/app-constants';
import { LockOwner } from '../../components/shared/models/lock-owner.model';
import { SiteConfig } from '../../types';
import { FEATURE_FLAGS } from '../../constants/launch-darkly-feature-flags.constants';

const {
  VALIDATION_STATUS: { IN_PROGRESS, VALID, INVALID },
  workingCopyAspect,
} = CommonConstants;

export function canCheckout(context: RuleContext): boolean {
  if (context.selection.count) {
    return selectedFilesCheckedOut(context);
  }
  return false;
}

export function canDownloadSelectedFiles(context: RuleContext): boolean {
  if (!context.selection.isEmpty && navigation.isNotTrashcan(context)) {
    return context.selection.nodes.every(
      (node: any) =>
        node.entry &&
        (node.entry.isFile || node.entry.isFolder || !!node.entry.nodeId) &&
        !haveAspectNamesOrProperties(node) &&
        haveLockType(node)
    );
  }
  return false;
}

export function canCheckin(context: RuleContext): boolean {
  let userRole: string | undefined;

  if (
    !context.selection.isEmpty &&
    navigation.isNotTrashcan(context) &&
    navigation.isNotRecentFiles(context) &&
    navigation.isNotFavorites(context) &&
    navigation.isNotSharedFiles(context)
  ) {
    context['store']
      .select(getCurrentSiteUserRole)
      .pipe(take(1))
      .subscribe((currentSiteUserRole: string) => {
        userRole = currentSiteUserRole;
      });

    if (userRole === 'SiteContributor') {
      return context.selection.nodes.every(
        (node) =>
          node.entry.isFile &&
          haveAspectNamesOrProperties(node) &&
          isUserWorkingCopyOwner(node, context.profile.id)
      );
    }

    return context.selection.nodes.every(
      (node) => node.entry.isFile && haveAspectNamesOrProperties(node)
    );
  }
  return false;
}

export function canBatchMetadataUpdate(context: RuleContext): boolean {
  if (context.selection.count > 1) {
    return selectedFilesCheckedOut(context);
  }
  if (context.selection.first && context.selection.first.entry.isFolder) {
    return true;
  }
  return false;
}

export function canStartProcessAndCheckout(context: RuleContext): boolean {
  if (
    !context.selection.isEmpty &&
    navigation.isNotTrashcan(context) &&
    navigation.isNotRecentFiles(context) &&
    navigation.isNotFavorites(context) &&
    navigation.isNotSharedFiles(context)
  ) {
    return context.selection.nodes.every(
      (node) =>
        node.entry.isFile &&
        !haveAspectNamesOrProperties(node) &&
        haveLockType(node)
    );
  }
  return false;
}

const haveAspectNamesOrProperties = (node: NodeEntry) =>
  node.entry.aspectNames?.includes(CommonConstants.workingCopyAspect) ||
  (node.entry.properties &&
    node.entry.properties[CommonConstants.workingCopyProperty]);

const haveLockType = (node: NodeEntry) =>
  !node.entry.isLocked &&
  !(node.entry.properties && isReadOnlyOrWriteLock(node));

const isReadOnlyOrWriteLock = (node: NodeEntry) =>
  node.entry.properties[CommonConstants.lockProperty] ===
    CommonConstants.readLock ||
  node.entry.properties[CommonConstants.lockProperty] ===
    CommonConstants.writeLock;

/**
 * temporary fix to bypass sonar deprecated issue
 *
 * @param context
 * @returns boolean
 */
export const canToggleFavorite = (context: RuleContext): boolean =>
  [
    [canAddFavorite(context), canRemoveFavorite(context)].some(Boolean),
    [
      navigation.isRecentFiles(context),
      navigation.isSharedFiles(context),
      navigation.isSearchResults(context),
      navigation.isFavorites(context),
    ].some(Boolean),
  ].every(Boolean);

const selectedFilesCheckedOut = (context: RuleContext): boolean =>
  context.selection.nodes.every((node) => {
    if (!node.entry.isFile) {
      return true;
    }
    if (!node.entry.aspectNames) {
      return false;
    }
    const isNotWorkingCopy =
      node.entry.aspectNames.indexOf(CommonConstants.workingCopyAspect) === -1;
    if (isNotWorkingCopy) {
      return true;
    }
    return (
      node.entry.aspectNames.indexOf(CommonConstants.workingCopyAspect) === -1
    );
  });

export const isModeEdit = (context: RuleContext): boolean =>
  context.navigation.url?.includes('/edit') ?? false;

export const isNotTrashcan = (context: RuleContext): boolean =>
  navigation.isNotTrashcan(context);

export const canShowCompareAndMergeOption = (context: RuleContext): boolean =>
  context?.selection?.nodes[0]?.entry?.content?.mimeType === 'text/html' &&
  canUploadVersion(context);

export function canDeleteSelection(context: RuleContext): boolean {
  if (
    navigation.isNotTrashcan(context) &&
    navigation.isNotLibraries(context) &&
    !context.selection.isEmpty
  ) {
    if (hasLockedFiles(context)) {
      return false;
    }

    // temp workaround for Favorites api
    if (navigation.isFavorites(context)) {
      return true;
    }

    if (navigation.isPreview(context)) {
      return context.permissions.check(context.selection.nodes, ['delete']);
    }

    // workaround for Shared Files
    if (navigation.isSharedFiles(context)) {
      return context.permissions.check(context.selection.nodes, ['delete'], {
        target: 'allowableOperationsOnTarget',
      });
    }

    return context.permissions.check(context.selection.nodes, ['delete']);
  }
  return false;
}

export function hasLockedFiles(context: RuleContext): boolean {
  if (context && context.selection && context.selection.nodes) {
    return context.selection.nodes.some((node) => {
      if (!node.entry.isFile) {
        return false;
      }

      return (
        node.entry.isLocked ||
        (node.entry.properties &&
          node.entry.properties[CommonConstants.lockProperty] ===
            CommonConstants.readLock)
      );
    });
  }

  return false;
}

export const isAllDocumentsDashboardLink = (context: RuleContext) =>
  context.navigation.url?.slice(1).startsWith('all-docs-dashboard');

function hasWorkflowPermission(
  context: RuleContext,
  currentUser: UserRepresentation,
  isSuperAdmin: boolean,
  apsRole: string[]
): boolean {
  const propertiesCheck = context.selection.file?.entry?.properties;

  return (
    propertiesCheck?.[CommonConstants.processId]?.length > 0 &&
    (propertiesCheck[CommonConstants.stepAssignedUser][0] ===
      currentUser?.email ||
      currentUser?.groups?.some((item) => apsRole.includes(item.name ?? '')) ||
      isSuperAdmin)
  );
}

// Separate from hasWorkflowPermission due to different process check
// and different permission logic (excludes step owner check)
function canControlWorkflow(
  context: RuleContext,
  currentUser: UserRepresentation,
  isSuperAdmin: boolean,
  apsRole: string[]
): boolean {
  const propertiesCheck = context.selection.file?.entry?.properties;

  return (
    propertiesCheck?.[CommonConstants.processId]?.length > 0 &&
    (currentUser?.groups?.some((item) => apsRole.includes(item.name ?? '')) ||
      isSuperAdmin)
  );
}

export function canUseValidationActions(context: RuleContext): boolean {
  if (!context?.['store'] || !context?.['appConfig']?.config?.sites) {
    return false;
  }

  let currentUser: UserRepresentation | undefined;
  let isSuperAdmin = false;

  context['store']
    .select(getApsUserProfile)
    .pipe(take(1))
    .subscribe((userInfo: UserRepresentation) => (currentUser = userInfo));

  context['store']
    .select(isAdmin)
    .pipe(take(1))
    .subscribe((admin: boolean) => (isSuperAdmin = admin));

  const apsRole =
    getCurrentSiteConfig(context['appConfig'].config.sites)?.userGroups
      ?.workflowManagerRole ?? [];

  return Boolean(
    isSuperAdmin ||
      currentUser?.groups?.some(
        (item) => typeof item.name === 'string' && apsRole.includes(item.name)
      )
  );
}

function canWorkflowAction(context: RuleContext): boolean {
  let currentUser: UserRepresentation;
  let isSuperAdmin = false;
  let apsRole: string[] = [];
  const selectedNodes = context.selection?.nodes?.length
    ? context.selection.nodes
    : context.selection?.file
    ? [context.selection.file]
    : [];

  if (
    selectedNodes.length &&
    navigation.isNotTrashcan(context) &&
    navigation.isNotRecentFiles(context) &&
    navigation.isNotFavorites(context) &&
    navigation.isNotSharedFiles(context) &&
    !isModeEdit(context)
  ) {
    context['store']
      .select(getApsUserProfile)
      .pipe(take(1))
      .subscribe((userInfo: UserRepresentation) => (currentUser = userInfo));

    context['store']
      .select(isAdmin)
      .pipe(take(1))
      .subscribe((admin: boolean) => (isSuperAdmin = admin));

    apsRole =
      getCurrentSiteConfig(context['appConfig'].config.sites)?.userGroups
        .workflowManagerRole ?? [];

    return selectedNodes.every((node) =>
      hasWorkflowPermission(
        {
          ...context,
          selection: {
            ...context.selection,
            file: node,
          },
        } as RuleContext,
        currentUser,
        isSuperAdmin,
        apsRole
      )
    );
  }
  return false;
}

// Separate from canWorkflowAction due to different permission checks:
// cancel requires canControlWorkflow (manager role only) vs assign requires hasWorkflowPermission (step owner or manager)
function canCancelWorkflowAction(context: RuleContext): boolean {
  let currentUser: UserRepresentation = {} as UserRepresentation;
  let isSuperAdmin = false;
  let apsRole: string[] = [];

  if (
    context.selection?.file &&
    navigation.isNotTrashcan(context) &&
    navigation.isNotRecentFiles(context) &&
    navigation.isNotFavorites(context) &&
    navigation.isNotSharedFiles(context) &&
    !isModeEdit(context)
  ) {
    context['store']
      .select(getApsUserProfile)
      .pipe(take(1))
      .subscribe((userInfo: UserRepresentation) => (currentUser = userInfo));

    context['store']
      .select(isAdmin)
      .pipe(take(1))
      .subscribe((admin: boolean) => (isSuperAdmin = admin));

    apsRole =
      getCurrentSiteConfig(context['appConfig'].config.sites)?.userGroups
        .workflowManagerRole ?? [];

    return canControlWorkflow(context, currentUser, isSuperAdmin, apsRole);
  }
  return false;
}

function canReassignWorkflowAction(context: RuleContext): boolean {
  const selectedNodes = context.selection?.nodes?.length
    ? context.selection.nodes
    : context.selection?.file
    ? [context.selection.file]
    : [];

  if (
    !selectedNodes.length ||
    !navigation.isNotTrashcan(context) ||
    !navigation.isNotRecentFiles(context) ||
    !navigation.isNotFavorites(context) ||
    !navigation.isNotSharedFiles(context) ||
    isModeEdit(context) ||
    !isAllDocumentsDashboardLink(context)
  ) {
    return false;
  }

  let currentUser: UserRepresentation;
  let isSuperAdmin = false;
  context['store']
    .select(getApsUserProfile)
    .pipe(take(1))
    .subscribe((userInfo: UserRepresentation) => (currentUser = userInfo));

  context['store']
    .select(isAdmin)
    .pipe(take(1))
    .subscribe((admin: boolean) => (isSuperAdmin = admin));

  const apsRole =
    getCurrentSiteConfig(context['appConfig'].config.sites)?.userGroups
      .workflowManagerRole ?? [];

  return selectedNodes.every((node) => {
    if (!node?.entry) {
      return false;
    }

    const file = node.entry;
    const processId = file?.properties?.[CommonConstants.processId];
    const stepAssignedUser =
      file?.properties?.[CommonConstants.stepAssignedUser]?.[0];

    if (!processId?.length || !stepAssignedUser) {
      return false;
    }

    const creatorCandidates = [
      file?.createdByUser?.id,
      file?.properties?.['cm:creator'],
    ].filter(Boolean);
    const isWorkflowOwner = creatorCandidates.includes(currentUser?.email);
    const isStepAssignee = currentUser?.email === stepAssignedUser;

    const isWorkflowManager =
      currentUser?.groups?.some((item) => apsRole.includes(item.name ?? '')) ||
      isSuperAdmin;
    const hasWorkflowAccess =
      isSuperAdmin || isWorkflowOwner || isWorkflowManager;

    return isWorkflowOwner || isStepAssignee || hasWorkflowAccess;
  });
}

export const canAssignWorkflowStep = (context: RuleContext): boolean =>
  canWorkflowAction(context);

export function canAssignWorkflowStepContextMenu(
  context: RuleContext
): boolean {
  if (!canWorkflowAction(context)) {
    return false;
  }

  const selectedNodes = context.selection?.nodes?.length
    ? context.selection.nodes
    : context.selection?.file
    ? [context.selection.file]
    : [];
  const assignWorkflowStepMode = getCurrentSiteConfig(
    context['appConfig'].config.sites
  )?.assignWorkflowStepMode;

  return !(assignWorkflowStepMode === 'taskDetail' && selectedNodes.length > 1);
}

export const canCancelWorkflow = (context: RuleContext): boolean =>
  canCancelWorkflowAction(context);

export const canReassignWorkflow = (context: RuleContext): boolean =>
  canReassignWorkflowAction(context);

export function canReassignTask(context: RuleContext): boolean {
  if (context.selection?.count >= 1) {
    if (isAllDocumentsDashboardLink(context) && context.selection.count > 1) {
      return false;
    }
    if (
      !context.selection.file?.entry ||
      !context['store'] ||
      !context['appConfig']?.config?.sites
    ) {
      return false;
    }

    const file = context.selection.file.entry;

    if (!file.properties?.['apollo:stepId']?.length) {
      return false;
    }

    let currentUser: UserRepresentation | undefined;
    let isSuperAdmin = false;
    context['store']
      .select(getApsUserProfile)
      .pipe(take(1))
      .subscribe((userInfo: UserRepresentation) => (currentUser = userInfo));

    context['store']
      .select(isAdmin)
      .pipe(take(1))
      .subscribe((admin: boolean) => (isSuperAdmin = admin));

    if (isSuperAdmin) {
      return true;
    }

    const stepAssignedUser =
      file.properties?.[CommonConstants.stepAssignedUser]?.[0];
    const isStepAssignee = currentUser?.email === stepAssignedUser;

    const creatorCandidates = [
      file.createdByUser?.id,
      file.properties?.['cm:creator'],
    ].filter(Boolean);
    const isWorkflowOwner = creatorCandidates.includes(currentUser?.email);

    const apsRole =
      getCurrentSiteConfig(context['appConfig'].config.sites)?.userGroups
        .workflowManagerRole ?? [];
    const isWorkflowManager =
      currentUser?.groups?.some((item) => apsRole.includes(item.name ?? '')) ??
      false;

    return isStepAssignee || isWorkflowOwner || isWorkflowManager;
  }

  return false;
}

export function canEditDocument(context: RuleContext): boolean {
  // check if selection and file exist and if the selection is equal to 1
  if (context.selection?.file && context.selection.count === 1) {
    // check if the UPDATE operation is allowed in the document
    if (
      !context.selection.file.entry.allowableOperations?.find(
        (operation) => operation === AllowableOperationsEnum.UPDATE
      )
    ) {
      // the document cant be edited/updated
      return false;
    }
    // get the aspect of checkout, if exist
    const checkedOutNodeAspect = context.selection.file.entry.aspectNames?.find(
      (aspect: string) =>
        aspect === AppConstants.CHECKEDOUT_ASPECT ||
        aspect === AppConstants.LOCKED_ASPECT
    );
    // check if the aspect exist to verify the owner of the document
    if (checkedOutNodeAspect) {
      const lockOwner = getLockOwner(context, checkedOutNodeAspect);
      // if the current user is not the owner who checkout the document return false
      return context.profile.id.toLowerCase() === lockOwner.id?.toLowerCase();
    } else {
      // return true if the document is not in checkout
      return true;
    }
  }
  // dafault case
  return false;
}

/**
 * Determines whether Download Validation List should be visible in Kebab menu
 * Only visible when validation status is INVALID.
 */
export function canDownloadValidationList(context: RuleContext): boolean {
  if (!context?.selection?.file) {
    return false;
  }
  const node = context.selection.file as any;
  if (!haveLockType(node)) {
    return false;
  }

  let isValidationEnabled = false;
  context['store']
    ?.select(getFeatureFlags)
    .pipe(take(1))
    .subscribe((flags) => {
      isValidationEnabled =
        !!flags?.[FEATURE_FLAGS.ENABLE_APOLLO_DOCUMENT_VALIDATION];
    });
  if (!isValidationEnabled) {
    return false;
  }

  // Only HTML documents support validation
  const fileName: string = node.entry?.name || '';
  if (!/.html/i.test(fileName)) {
    return false;
  }

  let currentStoreStatus = '';
  context['store']
    ?.select(getDocumentValidationStatus)
    .pipe(take(1))
    .subscribe((validationStatus: { status?: string }) => {
      currentStoreStatus = validationStatus?.status ?? '';
    });

  // During a new run, store status should take precedence over stale node metadata.
  if (currentStoreStatus === IN_PROGRESS) {
    return false;
  }

  // Show only when persisted node status is INVALID.
  const validationStatus =
    node.entry?.properties?.['apollo:currentValidationStatus'];
  if (!validationStatus || validationStatus !== INVALID) {
    return false;
  }

  return true;
}

const getLockOwner = (
  context: RuleContext,
  checkedOutNodeAspect: string
): LockOwner =>
  checkedOutNodeAspect === AppConstants.CHECKEDOUT_ASPECT
    ? context.selection.file?.entry.properties[AppConstants.WORKING_COPY_OWNER]
    : context.selection.file?.entry.properties[AppConstants.LOCK_OWNER];

export const isUserWorkingCopyOwner = (node: NodeEntry, userId: string) =>
  node.entry.properties['cm:workingCopyOwner']?.id === userId;

export const hasLockedNodes = (context: RuleContext): boolean =>
  context?.selection?.nodes?.some((node) =>
    node.entry.aspectNames?.includes(CommonConstants.lockableAspectName)
  );

export const hasActiveWorkflowNodes = (context: RuleContext): boolean =>
  context?.selection?.nodes?.some(
    (node) => node.entry.properties[CommonConstants.processId]?.length > 0
  );

export function isWorkingCopy(node) {
  if (node.aspectNames && node.aspectNames.indexOf('cm:workingcopy') !== -1) {
    return true;
  }
  return false;
}

export function canOpenWithOfficeCustom(context: RuleContext): boolean {
  if (!context || !context.selection) {
    return false;
  }

  const { file } = context.selection;

  if (!file || !file.entry) {
    return false;
  }

  if (isWorkingCopy(file.entry)) {
    return false;
  }

  return canOpenWithOffice(context as AcaRuleContext);
}

function getCurrentSiteConfig(sitesConfigs: SiteConfig[]): SiteConfig {
  const currentSiteName = localStorage.getItem(LocalStorage.CURRENT_SITE);
  const currentSiteConfig = find(sitesConfigs, { id: currentSiteName });
  const defaultSiteConfig = find(sitesConfigs, { id: 'default-site' });

  return currentSiteConfig || defaultSiteConfig;
}
export function canCreateFolder(context: AcaRuleContext): boolean {
  if (
    isContentServiceEnabled(context) &&
    (navigation.isPersonalFiles(context) ||
      navigation.isLibraryContent(context))
  ) {
    const { currentFolder } = context.navigation;

    if (currentFolder) {
      return context.permissions.check(currentFolder, ['create']);
    }
  }
  return false;
}

export function canValidate(context: RuleContext): boolean {
  return context?.selection?.nodes?.some(
    (node) =>
      !node.entry.aspectNames?.includes(workingCopyAspect) &&
      /.html/i.test(node.entry.name || '') &&
      ![IN_PROGRESS, VALID].includes(
        node.entry.properties['apollo:currentValidationStatus']
      )
  );
}
